import { useCallback, useRef, type Dispatch, type SetStateAction } from 'react';

import { fetchPropertyAddresses } from '../api/client';
import {
  ACCOUNT_ID_LABEL_ID,
  PROPERTY_ADDRESS_LABEL_ID,
  PROPERTY_ADDRESS_NOT_APPLICABLE_VALUE,
} from '../constants/app';
import {
  ILabel,
  ILabelOption,
  IPropertyAddressItem,
  IRequestType,
} from '../constants/interfaces';
import { FormState } from './useFormState';

interface UsePropertyAddressOptionsParams {
  requestType: IRequestType;
  setLabels: Dispatch<SetStateAction<ILabel[]>>;
  setFormState: Dispatch<SetStateAction<FormState>>;
  setSnackbar?: (snackbar: {
    message: string;
    open: boolean;
    severity: 'success' | 'error' | 'warning';
  }) => void;
}

export function formatPropertyAddressLabel(
  address: IPropertyAddressItem
): string {
  const street = address.property_address?.trim() ?? '';
  const city = address.property_city?.trim() ?? '';
  const state = address.property_state?.trim() ?? '';
  const zip = address.property_zip?.trim() ?? '';
  const servicerPropertyId = address.servicer_property_id?.trim() ?? '';

  const stateZip = [state, zip].filter(Boolean).join(' ');
  const cityStateZip =
    city && stateZip ? `${city}, ${stateZip}` : city || stateZip;

  const parts = [street, cityStateZip].filter(Boolean);
  let label = parts.join(', ');

  if (servicerPropertyId) {
    label = label
      ? `${label} (${servicerPropertyId})`
      : `(${servicerPropertyId})`;
  }

  return label;
}

function addressesToOptions(addresses: IPropertyAddressItem[]): ILabelOption[] {
  return addresses.map((address, index) => ({
    isDefault: false,
    labelOptionId: address.property_details_id,
    orderIndex: (index + 1) * 10,
    parentLabelId: 0,
    text: formatPropertyAddressLabel(address),
    value: String(address.property_details_id),
  }));
}

export function getNotApplicablePropertyAddressOption(): ILabelOption {
  return {
    isDefault: true,
    labelOptionId: -1,
    orderIndex: 10,
    parentLabelId: 0,
    text: PROPERTY_ADDRESS_NOT_APPLICABLE_VALUE,
    value: PROPERTY_ADDRESS_NOT_APPLICABLE_VALUE,
  };
}

export function getOtherPropertyAddressOption(): ILabelOption {
  return {
    isDefault: false,
    labelOptionId: -2,
    orderIndex: 999,
    parentLabelId: 0,
    text: 'Other',
    value: 'Other',
  };
}

export function buildPropertyAddressOptions(
  addresses: IPropertyAddressItem[]
): ILabelOption[] {
  const baseOptions =
    addresses.length === 0
      ? [getNotApplicablePropertyAddressOption()]
      : addressesToOptions(addresses);

  return [...baseOptions, getOtherPropertyAddressOption()];
}

export function resolvePropertyAddressFormValue(
  addresses: IPropertyAddressItem[],
  currentValue: string
): string {
  if (addresses.length === 0) {
    return PROPERTY_ADDRESS_NOT_APPLICABLE_VALUE;
  }

  const validPropertyDetailIds = addresses.map((address) =>
    String(address.property_details_id)
  );

  return validPropertyDetailIds.includes(currentValue) ? currentValue : '';
}

function updatePropertyAddressLabel(
  labels: ILabel[],
  options: ILabelOption[]
): ILabel[] {
  return labels.map((label) =>
    label.labelId === PROPERTY_ADDRESS_LABEL_ID ? { ...label, options } : label
  );
}

export function usePropertyAddressOptions({
  requestType,
  setLabels,
  setFormState,
  setSnackbar,
}: UsePropertyAddressOptionsParams) {
  const lastFetchedAccountIdRef = useRef<string | null>(null);
  const isPropertyPreservation = requestType.name === 'Property Preservation';

  const clearPropertyAddressOptions = useCallback(() => {
    setLabels((prev) => updatePropertyAddressLabel(prev, []));
    setFormState((prev) => ({
      ...prev,
      [PROPERTY_ADDRESS_LABEL_ID]: {
        ...prev[PROPERTY_ADDRESS_LABEL_ID],
        value: '',
      },
    }));
  }, [setFormState, setLabels]);

  const handleAccountIdBlur = useCallback(
    async (accountId: string) => {
      if (!isPropertyPreservation) {
        return;
      }

      const trimmedAccountId = accountId.trim();
      if (!trimmedAccountId) {
        lastFetchedAccountIdRef.current = null;
        clearPropertyAddressOptions();
        return;
      }

      if (trimmedAccountId === lastFetchedAccountIdRef.current) {
        return;
      }

      try {
        const response = await fetchPropertyAddresses(trimmedAccountId);
        const addresses = response.data.addresses ?? [];
        lastFetchedAccountIdRef.current = trimmedAccountId;

        const options = buildPropertyAddressOptions(addresses);

        setLabels((prev) => updatePropertyAddressLabel(prev, options));
        setFormState((prev) => {
          const currentValue = String(
            prev[PROPERTY_ADDRESS_LABEL_ID]?.value ?? ''
          );
          const nextValue = resolvePropertyAddressFormValue(
            addresses,
            currentValue
          );

          if (nextValue === currentValue) {
            return prev;
          }

          return {
            ...prev,
            [PROPERTY_ADDRESS_LABEL_ID]: {
              ...prev[PROPERTY_ADDRESS_LABEL_ID],
              value: nextValue,
            },
          };
        });
      } catch (error: unknown) {
        lastFetchedAccountIdRef.current = null;
        clearPropertyAddressOptions();

        const apiMessage =
          typeof error === 'object' &&
          error !== null &&
          'response' in error &&
          typeof (error as { response?: { data?: unknown } }).response?.data ===
            'string'
            ? ((error as { response?: { data?: string } }).response?.data ?? '')
            : error instanceof Error
              ? error.message
              : 'Unable to load property addresses.';

        setSnackbar?.({
          message: apiMessage,
          open: true,
          severity: 'error',
        });
      }
    },
    [
      clearPropertyAddressOptions,
      isPropertyPreservation,
      setFormState,
      setLabels,
      setSnackbar,
    ]
  );

  const handleFieldBlur = useCallback(
    (labelId: number, value: string) => {
      if (labelId === ACCOUNT_ID_LABEL_ID) {
        void handleAccountIdBlur(value);
      }
    },
    [handleAccountIdBlur]
  );

  return { handleFieldBlur };
}
